import { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2, Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { CreateFilterGroupDialog } from "./CreateFilterGroupDialog";
import { ManageFilterGroupItemsDialog } from "./ManageFilterGroupItemsDialog";
import type { FilterGroup, FilterGroupItem } from "@/stores/admin/repository";

interface FilterGroupUpdate {
  label: string;
  label_et: string | null;
  linked_category_id: number | null;
}

const FilterHierarchyTable = observer(() => {
  const { documentCategoriesStore } = useStores();
  const [groupUpdates, setGroupUpdates] = useState<Record<number, FilterGroupUpdate>>({});
  const [savingGroup, setSavingGroup] = useState<number | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [manageItemsGroupId, setManageItemsGroupId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: number; label: string } | null>(null);
  const [itemCounts, setItemCounts] = useState<Record<number, number>>({});
  const [allItems, setAllItems] = useState<FilterGroupItem[]>([]);

  // Load filter groups and all categories on mount
  useEffect(() => {
    documentCategoriesStore.loadFilterGroups();
    documentCategoriesStore.loadCategories();
  }, [documentCategoriesStore]);

  // Load item counts for each filter group
  useEffect(() => {
    const loadItemCounts = async () => {
      const counts: Record<number, number> = {};
      const items: FilterGroupItem[] = await documentCategoriesStore.loadAllFilterGroupItems();
      setAllItems(items);

      for (const group of documentCategoriesStore.filterGroupsList) {
        counts[group.id] = items.filter((item) => item.filter_group_id === group.id).length;
      }
      setItemCounts(counts);
    };

    if (documentCategoriesStore.filterGroupsList.length > 0) {
      loadItemCounts();
    }
  }, [documentCategoriesStore, documentCategoriesStore.filterGroupsList.length]);

  // Initialize local state with current filter groups
  useEffect(() => {
    const updates: Record<number, FilterGroupUpdate> = {};
    documentCategoriesStore.filterGroupsList.forEach((group) => {
      updates[group.id] = {
        label: group.label,
        label_et: group.label_et ?? null,
        linked_category_id: group.linked_category_id,
      };
    });
    setGroupUpdates(updates);
  }, [documentCategoriesStore.filterGroupsList]);

  const handleSave = async (groupId: number) => {
    const update = groupUpdates[groupId];
    if (!update) return;

    setSavingGroup(groupId);
    try {
      await documentCategoriesStore.updateFilterGroup(groupId, update);
      toast.success("Filter group updated successfully");
    } catch (error) {
      toast.error("Failed to update filter group");
    } finally {
      setSavingGroup(null);
    }
  };

  const handleDeleteClick = useCallback((groupId: number, label: string) => {
    setGroupToDelete({ id: groupId, label });
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!groupToDelete) return;

    setDeletingGroup(groupToDelete.id);
    try {
      await documentCategoriesStore.deleteFilterGroup(groupToDelete.id);
      toast.success("Filter group deleted successfully");
    } catch (error) {
      toast.error("Failed to delete filter group");
    } finally {
      setDeletingGroup(null);
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
    }
  }, [groupToDelete, documentCategoriesStore]);

  const handleFieldChange = (
    groupId: number,
    field: keyof FilterGroupUpdate,
    value: string | number | null
  ) => {
    setGroupUpdates((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [field]: value,
      },
    }));
  };

  const handleItemsSaved = useCallback(async () => {
    // Reload item counts after items are changed
    const items: FilterGroupItem[] = await documentCategoriesStore.loadAllFilterGroupItems();
    setAllItems(items);

    const counts: Record<number, number> = {};
    for (const group of documentCategoriesStore.filterGroupsList) {
      counts[group.id] = items.filter((item) => item.filter_group_id === group.id).length;
    }
    setItemCounts(counts);
  }, [documentCategoriesStore]);

  // Filter groups based on search query
  const filteredGroups = documentCategoriesStore.filterGroupsList.filter((group) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      group.label.toLowerCase().includes(query) ||
      (group.label_et && group.label_et.toLowerCase().includes(query)) ||
      (group.linked_category_name && group.linked_category_name.toLowerCase().includes(query))
    );
  });

  // Get the filter group being managed
  const manageItemsGroup = manageItemsGroupId
    ? documentCategoriesStore.filterGroupsList.find((g) => g.id === manageItemsGroupId)
    : null;

  if (documentCategoriesStore.isLoadingFilterGroups) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (documentCategoriesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading filter groups: {documentCategoriesStore.error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-dashed bg-muted/30 p-4">
        <p className="text-sm text-muted-foreground">
          <strong>Filter Hierarchy</strong> is decoupled from the Classification Pipeline hierarchy.
          Create filter groups to organize how categories appear in the document filter UI. Each
          category can belong to only one filter group.
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Search filter groups..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Filter Group
        </Button>
      </div>

      {filteredGroups.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim()
            ? "No filter groups match your search."
            : "No filter groups found. Click the button above to create your first filter group."}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table className="w-full" style={{ tableLayout: "fixed" }}>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: "25%" }}>Label</TableHead>
                <TableHead style={{ width: "25%" }}>Label (ET)</TableHead>
                <TableHead style={{ width: "20%" }}>Linked Category</TableHead>
                <TableHead style={{ width: "10%" }}>Items</TableHead>
                <TableHead style={{ width: "20%" }} className="text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.map((group) => {
                const update = groupUpdates[group.id];
                if (!update) return null;

                const hasChanges =
                  update.label !== group.label ||
                  update.label_et !== (group.label_et ?? null) ||
                  update.linked_category_id !== group.linked_category_id;

                return (
                  <TableRow key={group.id}>
                    <TableCell style={{ width: "25%" }}>
                      <Input
                        value={update.label}
                        onChange={(e) => handleFieldChange(group.id, "label", e.target.value)}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell style={{ width: "25%" }}>
                      <Input
                        value={update.label_et ?? ""}
                        onChange={(e) =>
                          handleFieldChange(group.id, "label_et", e.target.value || null)
                        }
                        className="h-8"
                        placeholder="Estonian label"
                      />
                    </TableCell>
                    <TableCell style={{ width: "20%" }}>
                      <Select
                        value={update.linked_category_id?.toString() ?? "none"}
                        onValueChange={(value) =>
                          handleFieldChange(
                            group.id,
                            "linked_category_id",
                            value === "none" ? null : parseInt(value)
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue placeholder="No linked category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No linked category</SelectItem>
                          {documentCategoriesStore.categoriesList.map((category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.category_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell style={{ width: "10%" }}>
                      <span className="text-sm text-muted-foreground">
                        {itemCounts[group.id] ?? 0} categories
                      </span>
                    </TableCell>
                    <TableCell style={{ width: "20%" }} className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setManageItemsGroupId(group.id)}
                          title="Manage items"
                          className="relative"
                        >
                          <Settings2 className="h-4 w-4" />
                          {(itemCounts[group.id] ?? 0) > 0 && (
                            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSave(group.id)}
                          disabled={!hasChanges || savingGroup === group.id}
                        >
                          {savingGroup === group.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Save"
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteClick(group.id, group.label)}
                          disabled={deletingGroup === group.id}
                        >
                          {deletingGroup === group.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateFilterGroupDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        categories={documentCategoriesStore.categoriesList}
      />

      <ManageFilterGroupItemsDialog
        open={manageItemsGroupId !== null}
        onOpenChange={(open) => {
          if (!open) setManageItemsGroupId(null);
        }}
        filterGroup={manageItemsGroup ?? null}
        allItems={allItems}
        onItemsSaved={handleItemsSaved}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Filter Group"
        description={`Are you sure you want to delete "${groupToDelete?.label}"? This will remove all items from this group. This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
});

export default FilterHierarchyTable;
